import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import { Button } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import CheckInCircleIcon from "~/components/Icons/svg/checkincircle";
import CheckInFileIcon from "~/components/Icons/svg/checkinfile";
import PrintIcon from "~/components/Icons/svg/print";
import { useGetActiveRequisite } from "~/entity/AvtovazRequisites";
import { UpdateContractIProps, usePrintTemplate } from "~/entity/Contract";
import { useTemplateOptions } from "~/pages/ListOfDocuments/hooks/useTemplateOptions";
import { useTrustedPersons } from "~/pages/ListOfDocuments/hooks/useTrustedPersons";
import { formDocs } from "~/pages/ListOfDocuments/Modals/options";
import { formResolverContract } from "~/pages/ListOfDocuments/schema";

import SelectedField from "./index";

import styles from "./../style.less";

export const contractFormId = "contractForm";

interface FormContractIProps {
  defaultValues;
  submit: (data: UpdateContractIProps) => void;
  dealerId?: number;
  contractId?: number;
  printContract?: boolean;
}

const FormContract = ({
  defaultValues,
  submit,
  dealerId,
  contractId,
  printContract,
}: FormContractIProps) => {
  const [requisites, setRequisites] = useState([]);
  const templates = useTemplateOptions();
  const { print } = usePrintTemplate();
  const { data } = useGetActiveRequisite();
  const { t } = useTranslation();
  const { control, watch, setValue, handleSubmit, errors, trigger, getValues } =
    useForm({
      defaultValues: defaultValues,
      resolver: yupResolver(formResolverContract(t)),
    });
  const { dataOptions } = useTrustedPersons();

  useEffect(() => {
    if (data) {
      const newData = data.map((item) => {
        return { value: item.id, label: item.paymentAccount };
      });
      setRequisites([...newData]);
    }
  }, [data]);

  useEffect(() => {
    const acc = watch("avtovazAccount");

    if (acc && data) {
      setValue("vaz_requisitesId", acc?.value ? acc.value : "");
      trigger("vaz_requisitesId");
      const item = data.find((item) => item.id === acc.value);
      setValue("avtovazBank", item?.bankName ? item.bankName : "");
      trigger("avtovazBank");
    }
  }, [data, setValue, watch("avtovazAccount")]);

  const addSignedByDealer = () => {
    setValue("dealerSignedDate", new Date());
    trigger("dealerSignedDate");
  };
  const addWorkStartDate = () => {
    setValue("workStartDate", new Date());
    trigger("workStartDate");
  };

  return (
    <form
      id={contractFormId}
      className={styles.formWrapper}
      onSubmit={handleSubmit(submit)}
    >
      <div className={styles.wrapperBlocks}>
        {formDocs().map(({ title, fields }) => {
          return (
            <div key={title} className={styles.titleBlockDocs}>
              <span className={styles.itemBlockTitle}>{title}</span>
              <div className={styles.itemWrappParent}>
                {fields.map((items) => {
                  return (
                    <div
                      key={JSON.stringify(items)}
                      className={styles.itemsBlock}
                    >
                      {items.map((item) => {
                        return (
                          <SelectedField
                            key={JSON.stringify(item)}
                            field={item.field}
                            type={item.type}
                            control={control}
                            name={item.name}
                            title={item.title}
                            position={item?.position ?? null}
                            readOnly={item?.readOnly}
                            errors={errors}
                            requisites={requisites}
                            templates={templates}
                            dataOptions={dataOptions}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <Flex gap={10} wrap>
        {dealerId && (
          <Button
            color="primary"
            bold
            transform="uppercase"
            disabled={!printContract}
            icon={<PrintIcon />}
            style={{ order: 1 }}
            size={"medium"}
            onClick={() => {
              print({
                contractId: contractId,
                dealerId: dealerId,
                requisitesId: defaultValues.vaz_requisitesId,
                contractNumber: watch("contractNumber"),
              });
            }}
          >
            Генерация договора
          </Button>
        )}
        <Button
          color="secondary"
          bold
          transform="uppercase"
          icon={<CheckInFileIcon />}
          style={{ order: dealerId ? 3 : 1 }}
          onClick={addWorkStartDate}
          size={"medium"}
        >
          Договор включен в работу
        </Button>
        <Button
          color="secondary"
          bold
          transform="uppercase"
          icon={<CheckInCircleIcon />}
          style={{ order: 2 }}
          onClick={addSignedByDealer}
          size={"medium"}
        >
          Подписано дилером
        </Button>
      </Flex>
    </form>
  );
};

export default FormContract;
